import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import TextField from 'components/form-components/TextField';
import searchIcon from 'assets/images/icons/search.svg';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import FormButton from 'components/form-components/FormButton';
import MultiLevelSelect from 'components/form-components/MultiLevelSelect';
import DatabaseExportModal from '../DatabaseExportModal';
import { CallAPI } from 'actions/General';
import SortingDropdown from 'components/form-components/SortingDropdown';
import DashboardSetupActions from '../DashboardSetupActions';
import { isCustomPermissionAvailable, isPermissionAvailable } from 'config/permissionUtils';
import { permissionKeys } from 'config/config';
import PowerBIViewModal from './PowerBIViewModal';

const FormsSetsHeaders = ({
  selectedFormSets,
  handleSync,
  setsOptions,
  handleManageSets,
  selectedTab,
  headersList,
  listingAPIOptions,
  updateListingApiParams,
  actionsCallBack,
  selectedRecordIds,
  topicList,
  formSets,
  selectedRows,
  updateApiBody,
  setUpdateApiBody,
  setMetaDataList,
  unSavedChanges,
}) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [sortByOptions, setSortByOptions] = useState([]);
  const [showDatabaseExportModal, setShowDatabaseExportModal] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPowerBIModal, setShowPowerBIModal] = useState(false);

  const modifiedSetsOptions = {
    selectMultiple: true,
    searchable: true,
    children: setsOptions,
  };

  const getDisplayValue = (list, checkValue, defaultValue = '') => {
    return list.find((o) => o.value === checkValue)?.displayValue ?? defaultValue;
  };

  const getCompanyDetail = async () => {
    let result = await CallAPI('GET_COMPANY_SQL_EXPORT_DETAIL', null, null, setLoading, null, null);
    if (result.status) {
      setCompanyDetails(result.data);
    }
  };

  useEffect(() => {
    getCompanyDetail();
  }, []);

  useEffect(() => {
    if (headersList.length) {
      if (selectedTab === 'forms_structure') {
        const optionsArray = [];
        headersList.forEach((header) => {
          optionsArray.push({
            value: header.name,
            displayValue: header.displayName,
          });
        });
        setSortByOptions(optionsArray);
      } else {
        setSortByOptions([]);
      }
    }
  }, [headersList]);

  return (
    <div className='p-3'>
      <div className='mt-2 d-flex justify-content-between align-items-end'>
        <div className='d-flex gap-3'>
          <TextField
            value={searchText}
            name='searchText'
            handleChange={(_, value) => setSearchText(value)}
            placeholder='field_search'
            icon={searchIcon}
            debounceFunc={(name, value) => updateListingApiParams({ [name]: value })}
            shrink
          />

          <DashboardSetupActions
            ids={selectedRecordIds}
            selectedRows={selectedRows}
            topicList={topicList}
            formSets={formSets}
            selectedTab={selectedTab}
            updateApiBody={updateApiBody}
            setUpdateApiBody={setUpdateApiBody}
            handleManageSets={handleManageSets}
            setMetaDataList={setMetaDataList}
            selectedFormSets={selectedFormSets}
          />
        </div>

        <div className='d-flex flex-shrink-0 gap-3 user-select-none align-items-end'>
          {/* <Export type={'user'} screenTab={selectedTab} /> */}

          {selectedTab === 'form_meta_and_sql' &&
            isPermissionAvailable(permissionKeys.system_dashboard_setup_update) && (
              <>
                <FormButton
                  text={'button_power_bi_view'}
                  onClick={() => setShowPowerBIModal(true)}
                  variant='green-2'
                  minWidth
                />

                <FormButton
                  text={'button_users'}
                  onClick={() => setShowDatabaseExportModal('company_user_sql')}
                  variant='green-2'
                  minWidth
                />
                <FormButton
                  text={'button_company_tasks'}
                  onClick={() => setShowDatabaseExportModal('company_task_sql')}
                  variant='green-2'
                  minWidth
                />
                <FormButton
                  text={'button_company_attendance'}
                  onClick={() => setShowDatabaseExportModal('company_attendance_sql')}
                  variant='green-2'
                  minWidth
                />
              </>
            )}

          {(() => {
            const isFormMetaTab = selectedTab === 'form_meta_and_sql';
            const isFormStructureTab = selectedTab === 'forms_structure';

            const hasPermission =
              (isFormMetaTab &&
                isCustomPermissionAvailable(
                  'system.dashboard_setup.dashboardSetupPermission.companySQL',
                )) ||
              (isFormStructureTab &&
                isCustomPermissionAvailable(
                  'system.dashboard_setup.dashboardSetupPermission.formStructureSQL',
                ));

            if (!hasPermission) return null;

            return isFormMetaTab ? (
              <FormButton
                text={'button_company_sql'}
                onClick={() => setShowDatabaseExportModal('companyExport')}
                variant='green-2'
                minWidth
              />
            ) : (
              <FormButton
                text={'button_form_structure'}
                onClick={() => setShowDatabaseExportModal('companyFormStructureExport')}
                variant='green-2'
                minWidth
              />
            );
          })()}

          {!loading && showDatabaseExportModal && (
            <DatabaseExportModal
              companyDetails={companyDetails}
              getCompanyDetail={getCompanyDetail}
              refreshList={actionsCallBack}
              handleClose={() => setShowDatabaseExportModal(null)}
              exportType={showDatabaseExportModal}
            />
          )}

          {!loading && showPowerBIModal && (
            <PowerBIViewModal
              handleClose={() => setShowPowerBIModal(false)}
              powerBIFormIds={(companyDetails?.powerBiFunctionSetting?.forms ?? []).map(
                (f) => f.formId,
              )}
              refreshList={getCompanyDetail}
            />
          )}

          {selectedTab === 'forms_structure' && (
            <SortingDropdown
              displayName='text_sort_by'
              options={sortByOptions}
              name='sortBy'
              directionKey='sortDirection'
              selectedValue={listingAPIOptions.sortBy}
              selectedDirection={listingAPIOptions.sortDirection}
              handleChange={(name, value) => updateListingApiParams({ [name]: value })}
              toggleClasses=''
              toggleComponent={
                <>
                  <label className='cursor-pointer'>
                    Sort By: {t(getDisplayValue(sortByOptions, listingAPIOptions.sortBy))}
                  </label>
                  <img src={chevronGreyIcon} className='rotate-180' />
                </>
              }
              scrollable
            />
          )}

          <FormButton
            text='button_save'
            onClick={handleSync}
            variant='green-1'
            disabled={
              !isPermissionAvailable(permissionKeys.system_dashboard_setup_update) ||
              !unSavedChanges
            }
          />

          <MultiLevelSelect
            options={modifiedSetsOptions}
            value={selectedFormSets || []}
            name='manageSets'
            disabled={modifiedSetsOptions.children.length === 0}
            handleChange={(name, value) => handleManageSets(value)}
            classes='theme-font-jakartaSans-regular'
            toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1 user-select-none'
            toggleComponent={
              <>
                <label className='cursor-pointer'>{t('select_manage_sets')}</label>
                <img className='rotate-180' src={chevronGreyIcon} />
              </>
            }
          />
        </div>
      </div>
    </div>
  );
};

FormsSetsHeaders.propTypes = {
  selectedFormSets: PropTypes.array.isRequired,
  handleSync: PropTypes.func.isRequired,
  setsOptions: PropTypes.array.isRequired,
  handleManageSets: PropTypes.func.isRequired,
  selectedTab: PropTypes.string.isRequired,
  headersList: PropTypes.array.isRequired,
  listingAPIOptions: PropTypes.object.isRequired,
  updateListingApiParams: PropTypes.func.isRequired,
  actionsCallBack: PropTypes.func.isRequired,
  selectedRecordIds: PropTypes.array.isRequired,
  topicList: PropTypes.array.isRequired,
  formSets: PropTypes.array.isRequired,
  selectedRows: PropTypes.array.isRequired,
  updateApiBody: PropTypes.object.isRequired,
  setUpdateApiBody: PropTypes.func.isRequired,
  setMetaDataList: PropTypes.func.isRequired,
  unSavedChanges: PropTypes.bool,
};

export default FormsSetsHeaders;
